import {
  BookOpen,
  Briefcase,
  CalendarClock,
  Check,
  ClipboardList,
  Download,
  FileText,
  Loader2,
  MessageSquare,
  Plus,
  Video,
  Wallet,
  X,
} from 'lucide-react'
import type {
  ReactNode
} from 'react'
import {
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  APP_TILE_ATTRIBUTE, AppTileFilter, useAppQuery, useAppTileVisible, useHideWhenNoTiles,
} from './appTileFilter'
import {
  integrationRequestService,
} from '../services/api'
import type {
  BuiltInAppMatch,
  CatalogAppMatch,
  IMessageHandleResponse,
  IntegrationInstance,
} from '../services/api'
import {
  downloadVCard,
} from '../utils/vcard'
import {
  CustomIntegrationsCard,
} from './CustomIntegrationsSection'
import {
  SharedIntegrationsCard,
} from './SharedIntegrationsSection'
import {
  OtherIntegrationsCard,
} from './WebhookIntegrationsSection'
import {
  DisconnectModal,
  DynamicIntegrationTile,
  PROMOTED_CATEGORY,
  PromotedCategoryCard,
  usePromotedIntegrations,
} from './PromotedIntegrations'
import {
  IMessageIcon,
  SlackIcon,
  SmsIcon,
  WhatsAppIcon,
} from './messaging-logos'
import {
  FeaturedAppIcon,
  GoogleDriveAccessModal,
  useFeaturedPipedreamApps,
} from './FeaturedPipedreamIntegrations'
import {
  TeamsAccessModal,
} from './TeamsAccessModal'
import type {
  FeaturedPipedreamApps,
} from './FeaturedPipedreamIntegrations'
import type {
  FeaturedIntegrationSummary,
} from '../client'
import {
  PipedreamAddAccountModal,
} from './PipedreamAddAccountModal'
import {
  asIntegrationInstances,
  canAddPipedreamAccount,
} from '../utils/pipedreamAccounts'
import {
  PipedreamIntegrationsCard,
} from './PipedreamIntegrationsCard'
import {
  CATEGORY_CARD_CLASS,
} from '../utils/cardStyles'
import {
  settingsSectionAnchor,
  type SettingsSectionId,
} from '../utils/settingsSections'
import {
  SuggestedIntegrationsCard,
} from './SuggestedIntegrationsCard'
import {
  IntegrationSuggestionSurfaces,
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'

// ─── Integration state shared interface ─────────────────────────────

export interface IntegrationState {
  connected: boolean
  loading: boolean
  detail?: string | undefined
  onConnect: () => void
  onDisconnect: () => void
  // Multi-instance support (OAuth integrations). When `instances` is provided,
  // a connected tile opens an "accounts" modal (one row per labeled instance,
  // each removable) with an "Add another" action instead of a single
  // disconnect. Single-instance integrations leave these undefined.
  instances?: IntegrationInstance[] | undefined
  onAddInstance?: (() => void) | undefined
  onRemoveInstance?: ((label: string) => void) | undefined
  // A connected tile that opens its own management surface instead of the
  // disconnect modal (see `IntegrationTile.onManage`).
  onManage?: (() => void) | undefined
}

export interface IntegrationCategoriesSectionProps {
  assistantName: string
  assistantPictureUrl?: string | null | undefined
  whatsapp: IntegrationState
  slack: IntegrationState
  // The owner's own read-only Slack connection (GET-14181), beside the
  // workspace (bot) install above. Null hides the tile.
  slackPersonal: IntegrationState | null
  // The permission on the Pipedream Microsoft Teams tile (GET-14326): what the
  // tile says once connected, and what to refresh after the picker saves. The
  // picker itself lives here, beside the featured hook whose disconnect it
  // owns. Null leaves the tile a plain featured tile.
  teamsAccess: { detail?: string | undefined; onSaved: () => Promise<void> } | null
  sms: IntegrationState | null
  smsPhoneNumber?: string | null | undefined
  imessage: IntegrationState | null
  imessagePhoneNumber?: string | null | undefined
  assistantVoiceNumber?: string | null | undefined
  imessageHandles?: IMessageHandleResponse[] | undefined
  onRemoveImessageHandle?: ((identifier: string) => void) | undefined
  zoom: IntegrationState | null
  googleMeet: IntegrationState | null
  googleDocs: IntegrationState | null
  granola: IntegrationState
  fathom: IntegrationState
  otter: IntegrationState
  wispr: IntegrationState
  clickup: IntegrationState | null
  asana: IntegrationState | null
  notion: IntegrationState | null
  hubspot: IntegrationState | null
  attio: IntegrationState | null
  zoho: IntegrationState | null
  salesforce: IntegrationState | null
  honeybook: IntegrationState | null
  customIntegrationBuilderEnabled?: boolean
  genericPipedreamIntegrationsEnabled?: boolean
  // Presentational only — see `FeatureFlags.PIPEDREAM_CATALOG_BROWSE`. Decides
  // whether the "Connect any app" browse surface renders, and therefore whether
  // the affordances that open it may be offered at all. Connected apps remain
  // visible through the capability-gated connection manager either way. Not the
  // whole answer on its own: see `shouldShowPipedreamCatalog`.
  pipedreamCatalogBrowseEnabled?: boolean
  // The app the assistant deep-linked the user here to connect, as a catalog
  // search term. See `shouldShowPipedreamCatalog`.
  pendingAppConnectQuery?: string | null | undefined
  // The user arrived on a `connect_app=google_drive` deep link. Opens the
  // access chooser rather than granting one of the two routes for them.
  pendingDriveAccess?: boolean
  pendingWebhook?: { name: string, description: string } | null | undefined
  // `settings-mail-beside-messaging` 'test' hands the Mail & Calendar card in
  // here so it renders as the catalog cell beside Messaging instead of in the
  // grid at the top of Settings. Owned entirely by SettingsPage — this section
  // only places it, and deliberately does NOT fold it into the abilities
  // progress bar below: the mail grant is an account card that moved, not a
  // new category, and counting it would make the two arms' "unlocked N of M"
  // denominators disagree.
  //
  // AFTER Messaging, never before it (GET-16318). It rendered first until
  // then, which took Messaging's top-left slot on the two-column grid: first
  // clicks on a Messaging tile fell from 21% to 9% of desktop visitors, and
  // Messaging connections per person halved, while each tile's own
  // click-to-connect rate was unchanged. Position, not a broken flow.
  besideMessagingCard?: ReactNode
  // Which slice to render. 'all' is the whole catalog (the single-page
  // Settings). The sidebar layout renders it twice: 'messaging' on its
  // "Where you chat" page and 'apps', with a search box, on its Apps page.
  part?: CatalogPart
}

export type CatalogPart = 'all' | 'messaging' | 'apps'

/**
 * "The browse catalog is available" — the single condition the browse surface
 * and every affordance that opens it must key off. Derived once, so the two can
 * never disagree.
 *
 * NOT whether the card renders. Since GET-13175 the card renders on the
 * capability flag alone, so a connected app stays visible and removable when
 * browsing is withheld; this predicate feeds its `catalogBrowseEnabled` prop.
 *
 * Two ways in, and only one of them is browsing. Opening the whole catalog
 * needs the presentational opt-in (`pipedream-catalog-browse`, an internal
 * cohort). Arriving on a slug the assistant already named is a *connect path*,
 * which that flag must never gate — and the card hard-gates BOTH its modal and
 * its `openRequest` effect on this prop, so without the second arm the link
 * `connect_integration` hands the user opens nothing at all, which is the
 * denial GET-12087 is about, relocated rather than fixed. Both arms still
 * require the capability flag, without which none of these apps can be
 * connected.
 *
 * Exported so that invariant is pinned by a test rather than by this comment.
 */
export function shouldShowPipedreamCatalog({
  capabilityEnabled,
  browseEnabled,
  pendingAppConnectQuery,
}: {
  capabilityEnabled: boolean
  browseEnabled: boolean
  pendingAppConnectQuery: string | null | undefined
}): boolean {
  return capabilityEnabled && (browseEnabled || Boolean(pendingAppConnectQuery))
}

// ─── Brand icons ────────────────────────────────────────────────────

function GoogleIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

function ZoomIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} fill="#2D8CFF" viewBox="0 0 24 24">
      <path d="M16,14.48l3.63,1.45A1,1,0,0,0,21,15V9a1,1,0,0,0-1.37-.93L16,9.52Z"/>
      <path d="M4,6h9a3,3,0,0,1,3,3v8a1,1,0,0,1-1,1H6a3,3,0,0,1-3-3V7A1,1,0,0,1,4,6Z"/>
    </svg>
  )
}

function GranolaIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 1308.244 1350" fill="none">
      <path fill="#1E1E1E" d="M1033.77,1021.552c-21.6,24.239-40.107,38.917-50.307,45.934-4.796,3.188-7.796,7.65-11.994,11.476-22.2,19.135-46.261,24.83-63.061,38.225-22.799,17.86-107.977,39.095-132.179,46.542-40.957,9.308-87.035,12.668-137.434,10.755-10.905,0-20.991-.005-30.259-.726-3.765-.293-7.538.675-11.314.723h-.423c-.401,0-1.069-.286-2.006-.856a6.37,6.37,0,0,0-3.509-1.06c-.325,0-.652-.03-.974-.073-5.08-.696-7.781,1.086-9.735,2.077-1.48.75-3.09.121-4.491-.767-4.431-2.813-14.317-9.136-17.682-10.161-3.317-1.008-3.642.369-5.405.679-1.181.208-2.41-.207-3.297-1.013-.994-.9-2.058-2.2-4.499-3.499-4.489-2.386-6.879,3.035-13.549-3.032-.969-.881-1.538-2.614-2.845-2.696-.328-.021-.565-.04-.887-.103-6.715-1.303-18.917-3.8-27.117-6.29-9.6-2.551-6.608-4.459-10.809-6.373-56.399-21.05-136.793-62.515-166.193-91.857-10.799-10.843-23.398-35.721-31.198-42.101-6-5.104-18.003-15.308-21.003-20.41-2.4-4.465-.004-12.754-4.203-18.495-5.4-7.017-16.202-10.847-26.402-26.794-11.399-17.859-17.999-41.46-29.398-65.698C201.997,854.911,175,786.02,175,660.361c0-84.199,39-200.934,55.8-216.88,10.796-10.207,9.596-32.529,17.393-43.372,89.012-123.746,244.804-214.786,430.204-224.355,7.533-.386,15.073-.627,22.615-.715,45.741-.534,91.583,4.469,136.037,15.311,44.414,10.832,86.873,27.735,128.261,46.951,0,0,4.914.389,6.21,1.026,2.159,1.061,3.071,2.995,5.229,4.058,2.159,1.061,5.28.161,7.639.644,7.774,1.592,9.17,6.208,10.602,8.047,1.739,2.234,3.83,3.087,7.784,4.222,10.312,2.96,11.668,6.368,13.071,7.937,1.12,1.255,1.608,2.881,2.167,4.339.568,1.482,1.695,2.835,3.277,3.206,3.416.801,8.063,4.983,9.025,10.689.627,3.72,4.649,5.323,3.55,12.301-.356,2.26,2.05,5.601-10.597,18.07s-39.184,20.328-55.34,14.135c-55.851-21.409-64.133-25.53-86.568-31.649-40.957-11.17-75.853-18.759-118.358-17.959-67.8,1.276-121.207,7.655-185.406,29.98-28.142,9.972-81.27,37.108-107.93,58.239-26.659,21.13-65.256,50.316-81.185,77.333-5.578,9.461-11.856,18.497-25.055,33.167-19.199,21.049-41.422,81.935-48.624,111.278-1.8,6.378,2.992,13.396.593,19.776-2.4,7.016-13.801,10.213-15.001,15.953-4.799,20.411-3.599,46.561-3.599,68.885,0,12.118,3.602,28.701,7.802,38.269,2.999,6.379,12.604,10.846,13.804,17.225.599,4.463-5.393,9.564-5.398,13.391,0,3.189,5.394,46.574,8.394,52.953,4.202,7.653,17.403,17.219,21.003,26.148,2.399,6.378-4.207,12.761.593,19.14,3,3.828,12.607,3.822,16.207,8.925,4.799,6.377,14.995,24.87,19.797,30.615,3.6,4.465,10.201,6.386,13.2,8.299,8.997,6.379,1.205,12.114,9.602,21.681,26.4,29.98,67.201,66.983,106.202,83.568,6.016,2.557,67.754,26.132,71.393,26.148,86.995,12.829,184.838,11.627,269.439-35.575,19.803-10.847,131.969-88.808,150.57-181.295,4.2-18.498,9.599-63.156,7.199-81.016-9.601-66.339-50.478-161.757-125.405-197.095-39.914-18.824-70.2-18.5-78-17.224-22.798,4.464-30.599-8.931-51.598-7.019-64.2,5.104-127.198,22.966-176.398,74.633-45,47.84-54.009,109.076-31.208,147.986,2.4,5.103,1.2,11.483-3.6,14.672-2.099,1.276-4.048,2.87-4.948,4.545-1.79,3.329,3.386,5.108,6.953,6.357,24.958,8.732,33.957,50.842,66.996,49.058h7.198s13.804-.004,19.204-6.383c4.438-5.242,4.418-11.347,1.273-14.063-1.403-1.211-3.176-1.934-3.588-3.741-.453-1.993-.68-4.615-.681-5.793,0-1.276,1.8-1.921,1.8-3.197-.003-3.826-4.2-7.015-3.6-10.84.385-2.037,3.213-4.854,5.208-6.957,1.516-1.598,1.537-3.634.545-5.6l-.106-.215c-.961-1.974-1.14-4.318-.487-6.413.376-1.204.832-2.491.833-3.777.599-5.74-1.792-8.293-2.393-12.756,0-1.576,8.545-5.319,11.559-7.66.889-.689.984-1.844.691-2.93-.623-2.315-1.452-3.033-1.452-7.27,0-1.019.861-2.444,1.895-3.789,2.084-2.71,4-5.598,4.941-8.885l1.659-5.787a6.78,6.78,0,0,1,4.918-5.146c4.134-1.391,2.217-8.13,6.155-10.008,1.146-.546,4.024.147,8.633-.833,9.595-1.913,2.999-5.103,4.796-10.205.839-3.124,3.441-2.814,5.956-2.565,2.016.201,3.98-.461,5.43-1.875,1.428-1.391,2.868-3.021,4.811-3.85,2.433-1.034,8.813-1.233,13.379-1.271,1.879-.016,3.744-.285,5.61-.505,5.096-.6,12.326-.244,15.819-.774,4.197-.64,6.596-4.466,10.194-4.467,3.001,0,7.206,5.102,10.205,5.102,3-.002,5.999-2.552,8.998-2.552,1.8.001,2.4,3.186,5.399,3.187h1.197s27.604.635,56.403,18.494c19.8,12.119,34.204,41.466,34.204,41.466,13.799,23.601-1.51,47.864-1.51,69.551,0,8.93,2.996,16.585,1.197,24.877-1.201,6.377-6.002,11.485-7.803,16.589-1.797,4.464-1.795,10.206-7.791,18.494-4.8,7.016-7.206,7.014-8.406,8.29-1.8,1.913-17.336,25.407-27.536,34.338-27,24.239-51.957,31.337-88.557,31.975-16.199.638-17.999,3.832-20.399,3.832-8.401.637-46.792-1.273-58.796-3.186,0,0-53.397-10.205-74.401-20.41-11.4-5.104-86.405-60.602-103.205-91.858-52.201-98.232-40.196-202.843,13.804-273.008,39-51.03,103.198-117.368,255.594-130.127,77.401-6.378,146.409,3.831,200.41,29.346,76.198,35.721,131.997,98.868,166.797,173.498C1154.801,743.282,1151.37,887.599,1033.77,1021.552Z"/>
    </svg>
  )
}

function FathomIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="141 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Fathom">
      <path d="M141.836 16.4481V21.3875C141.836 22.6831 142.645 23.8563 143.861 24.2456C145.927 24.9052 147.825 23.3578 147.825 21.3606V19.501L141.836 16.4481Z" fill="#007299"/>
      <path d="M162.465 15.4261C162.015 15.4261 161.559 15.3237 161.128 15.1055L143.553 6.19528C142.097 5.45682 141.42 3.67948 142.099 2.17906C142.807 0.60815 144.65 -0.0430392 146.16 0.722275L163.733 9.63081C165.207 10.3777 165.882 12.249 165.144 13.7444C164.619 14.8101 163.562 15.4261 162.462 15.4261H162.465Z" fill="#00BEFF"/>
      <path d="M153.644 19.9273C153.194 19.9273 152.737 19.8249 152.307 19.6068L143.553 15.1693C142.097 14.4308 141.42 12.6535 142.099 11.153C142.807 9.58214 144.65 8.93095 146.162 9.69626L154.913 14.1321C156.387 14.8789 157.062 16.7502 156.324 18.2456C155.799 19.3114 154.742 19.9273 153.642 19.9273H153.644Z" fill="#00BEFF"/>
    </svg>
  )
}

function ClickUpIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="5 8 118 112" fill="none">
      <path d="M19.079 87.058l14.737-11.2c8.237 10.842 16.695 15.943 28.737 15.943 11.89 0 20.676-5.22 28.842-15.791l14.585 11.382C95.011 101.27 81.58 110.2 62.553 110.2c-19.178 0-32.611-8.779-43.474-23.142z" fill="#7B68EE"/>
      <path d="M62.627 38.505L38.953 59.358l-13.358-14.84 37.032-33.316 36.558 33.316-13.36 14.84z" fill="#49CCF9"/>
    </svg>
  )
}

function AsanaIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M18.2 13.2c-2.1 0-3.8 1.7-3.8 3.8s1.7 3.8 3.8 3.8 3.8-1.7 3.8-3.8-1.7-3.8-3.8-3.8zm-12.4 0c-2.1 0-3.8 1.7-3.8 3.8S3.7 20.8 5.8 20.8s3.8-1.7 3.8-3.8-1.7-3.8-3.8-3.8zM12 3.2c-2.1 0-3.8 1.7-3.8 3.8s1.7 3.8 3.8 3.8 3.8-1.7 3.8-3.8S14.1 3.2 12 3.2z" fill="#F06A6A"/>
    </svg>
  )
}

function HubSpotIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none">
      <path
        fill="#FF7A59"
        d="M22.913 11.197V7.857a2.572 2.572 0 1 0-1.464 0v3.34a7.286 7.286 0 0 0-3.466 1.527L8.806 5.59A2.853 2.853 0 0 0 8.95 5a2.929 2.929 0 1 0-2.929 2.929c.494-.002.978-.13 1.41-.371l9.038 7.034a7.286 7.286 0 0 0 .111 8.21L13.83 25.6a2.4 2.4 0 0 0-.683-.111A2.4 2.4 0 1 0 15.55 27.9a2.4 2.4 0 0 0-.111-.683l2.722-2.723a7.318 7.318 0 1 0 4.752-13.297zm-.732 10.997a3.748 3.748 0 1 1 0-7.495 3.748 3.748 0 0 1 0 7.495z"
      />
    </svg>
  )
}

function SalesforceIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="#00A1E0"
        d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
      />
    </svg>
  )
}

// HoneyBook's official monogram (charcoal "HY"/"BK" on their honey yellow),
// taken from the brand-asset pack rather than redrawn — the first version of
// this component was hand-drawn and got the colour, the letters and the letter
// colour all wrong (GET-10588).
function HoneyBookIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/honeybook.png" alt="HoneyBook" className={`${className} rounded`} />
}

function AttioIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="7" fill="#0B0B0F" />
      <circle cx="16" cy="16" r="9.3" stroke="#ffffff" strokeWidth="2.2" />
      <circle cx="16" cy="16" r="4" fill="#ffffff" />
    </svg>
  )
}

function NotionIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 120 126" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21.1302 20.5616L76.0259 16.5124C82.7692 15.9334 84.5019 16.3207 88.7394 19.3985L106.262 31.7406C109.157 33.8587 110.121 34.4352 110.121 36.7482V112.435C110.121 116.677 108.579 119.176 103.18 119.561L39.4127 123.418C35.3678 123.611 33.4421 123.032 31.3236 120.333L18.4285 103.562C16.117 100.476 15.1543 98.1665 15.1543 95.4695V24.8036C15.1543 21.3395 16.6982 18.4549 21.1302 20.5616Z" fill="#ffffff"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M76.0259 16.5124L21.1302 20.5616C16.6982 18.4549 15.1543 21.3395 15.1543 24.8036V95.4695C15.1543 98.1665 16.117 100.476 18.4285 103.562L31.3236 120.333C33.4421 123.032 35.3678 123.611 39.4127 123.418L103.18 119.561C108.579 119.176 110.121 116.677 110.121 112.435V36.7482C110.121 34.5622 109.256 33.932 106.708 32.0643L106.262 31.7406L88.7394 19.3985C84.5019 16.3207 82.7692 15.9334 76.0259 16.5124ZM40.8362 40.0198C35.624 40.3713 34.4411 40.451 31.4798 38.0382L23.9593 32.0711C23.1947 31.2974 23.5797 30.3318 25.5073 30.1395L78.2755 27.0631C82.7065 26.6771 85.013 28.2217 86.7443 29.5736L95.7929 36.1199C96.1804 36.3136 97.1431 37.4725 95.9808 37.4725L41.5056 40.7418L40.8362 40.0198ZM34.7879 113.013V55.5876C34.7879 53.0808 35.5583 51.9193 37.8684 51.7252L100.294 48.0709C102.413 47.8785 103.374 49.2313 103.374 51.7347V108.774C103.374 111.285 102.989 113.403 99.5259 113.593L39.7974 117.056C36.3346 117.248 34.7879 116.1 34.7879 113.013ZM93.7572 58.6578C94.1427 60.3977 93.7572 62.1354 92.0219 62.3318L89.1411 62.9021V105.106C86.6371 106.449 84.3292 107.223 82.4028 107.223C79.3219 107.223 78.5497 106.258 76.2404 103.37L57.357 73.6832V102.405L63.3152 103.755C63.3152 103.755 63.3152 107.225 58.502 107.225L45.1977 107.996C44.812 107.224 45.1977 105.299 46.5477 104.914L50.0179 103.948V66.1538L45.19 65.7662C44.8026 64.0263 45.7632 61.5195 48.4737 61.3241L62.7466 60.3608L82.4028 90.4396V63.8317L77.4158 63.2572C77.0304 61.1347 78.5723 59.5902 80.4964 59.4007L93.7572 58.6578Z" fill="#111111"/>
    </svg>
  )
}

function MondayIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/monday.png" alt="Monday.com" className={`${className} rounded`} />
}

function ZohoIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/zoho.png" alt="Zoho" className={`${className} rounded`} />
}

function OtterIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/otter.png" alt="Otter.ai" className={`${className} rounded`} />
}

function WisprIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/wispr.png" alt="Wispr Flow" className={`${className} rounded`} />
}

// These are the brand assets Pipedream serves for each app (its
// `img_src`), resized to 128px, rather than redrawn — see HoneyBookIcon.
function QuickBooksIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/quickbooks.png" alt="QuickBooks" className={`${className} rounded`} />
}

function XeroIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/xero.png" alt="Xero" className={`${className} rounded`} />
}

function DocuSignIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/docusign.png" alt="DocuSign" className={`${className} rounded`} />
}

function GoHighLevelIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/highlevel.png" alt="GoHighLevel" className={`${className} rounded`} />
}

function ClioIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return <img src="/images/integrations/clio.png" alt="Clio" className={`${className} rounded`} />
}

// Dropbox's glyph, as Pipedream serves it.
function DropboxIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 235.45 200">
      <path fill="#0061FF" d="M58.86 0 0 37.5 58.86 75l58.87-37.5zM176.59 0l-58.86 37.5L176.59 75l58.86-37.5zM0 112.5 58.86 150l58.87-37.5L58.86 75zM176.59 75l-58.86 37.5L176.59 150l58.86-37.5zM58.86 162.5l58.87 37.5 58.86-37.5-58.86-37.5z"/>
    </svg>
  )
}

function GoogleDocsIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6z"/>
      <path fill="#F1F1F1" d="M14 2l6 6h-6V2z"/>
      <path fill="#F1F1F1" d="M8 12h8v1.5H8zm0 3h8v1.5H8zm0 3h5v1.5H8z"/>
    </svg>
  )
}

function GoogleDriveIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 87.3 78">
      <path fill="#0066da" d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"/>
      <path fill="#00ac47" d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z"/>
      <path fill="#ea4335" d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.5l5.85 11.5z"/>
      <path fill="#00832d" d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"/>
      <path fill="#2684fc" d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"/>
      <path fill="#ffba00" d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"/>
    </svg>
  )
}

function GoogleSheetsIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#0F9D58" d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6z"/>
      <path fill="#F1F1F1" d="M14 2l6 6h-6V2z"/>
      <path fill="#F1F1F1" d="M7.5 12h9v7h-9v-7zm1.25 1.25v1.25h2.5v-1.25h-2.5zm3.75 0v1.25h2.75v-1.25H12.5zm-3.75 2.5v1.25h2.5v-1.25h-2.5zm3.75 0v1.25h2.75v-1.25H12.5z"/>
    </svg>
  )
}

const TEAMS_APP_SLUG = 'microsoft_teams'
// Pinned beside Asana rather than appended with the other featured tiles, so it
// keeps the slot its "Coming soon" tile held.
const MONDAY_APP_SLUG = 'monday'

function MicrosoftTeamsIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="#5059C9" d="M16.6 9h4.7c.4 0 .7.3.7.7v4.5a3.4 3.4 0 0 1-3.4 3.4 3.4 3.4 0 0 1-3.4-3.4V9.6c0-.3.2-.6.6-.6h.8z"/>
      <circle fill="#5059C9" cx="19.2" cy="5.6" r="2"/>
      <circle fill="#7B83EB" cx="12.6" cy="5" r="2.7"/>
      <path fill="#7B83EB" d="M15.9 9H8.9c-.5 0-.9.4-.9.9v6.4a5 5 0 0 0 4.4 4.9 5 5 0 0 0 4.4-4.9V9.9c0-.5-.4-.9-.9-.9z"/>
      <path fill="#000000" opacity=".2" d="M12.9 8v9.6a.9.9 0 0 1-.8.8H8.1a5.7 5.7 0 0 1-.1-1.1V9.9c0-.5.4-.9.9-.9h4v-1z"/>
      <path fill="#4B53BC" d="M2.7 6.6h8.6c.7 0 1.2.5 1.2 1.2v8.6c0 .7-.5 1.2-1.2 1.2H2.7c-.7 0-1.2-.5-1.2-1.2V7.8c0-.7.5-1.2 1.2-1.2z"/>
      <path fill="#FFFFFF" d="M9.5 9.5h-5v1.3h1.8v5h1.5v-5h1.7V9.5z"/>
    </svg>
  )
}

function SharePointIcon({ className = 'w-8 h-8' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <circle fill="#036C70" cx="10" cy="7" r="5.5"/>
      <circle fill="#1A9BA1" cx="16" cy="12.5" r="5"/>
      <circle fill="#37C6D0" cx="12.5" cy="18.5" r="4"/>
      <path fill="#000000" opacity=".2" d="M12.3 6.5H5.6a8 8 0 0 0 0 11h6.7c.6 0 1.1-.5 1.1-1.1V7.6c0-.6-.5-1.1-1.1-1.1z"/>
      <path fill="#03787C" d="M1.5 7.4h9.6c.6 0 1.1.5 1.1 1.1v8.2c0 .6-.5 1.1-1.1 1.1H1.5V7.4z"/>
      <path fill="#FFFFFF" d="M5 15.6v-1.4c.3.2.5.3.8.4.3.1.6.1.8.1.2 0 .3 0 .5-.1l.3-.1.2-.2v-.6l-.2-.2-.4-.2-.5-.2-.9-.5a1.6 1.6 0 0 1-.6-1.3c0-.4.1-.7.2-1l.6-.6.8-.3H8.4l.6.2v1.3a2.4 2.4 0 0 0-1.3-.4h-.4l-.3.1-.2.2v.6l.2.2.3.2.5.2.6.3.4.3.3.4v1.4l-.2.6-.5.4-.7.2H6.2l-.7-.1-.5-.2z"/>
    </svg>
  )
}

// ─── Tile components ────────────────────────────────────────────────

function GoogleScopeInfoModal({
  name,
  onClose,
}: {
  name: string
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Managing {name} Access</h3>
        <p className="text-sm text-gray-500 mb-3">
          {name} is connected through your Google account. Google manages all permissions together, so individual services can&apos;t be disconnected separately.
        </p>
        <p className="text-sm text-gray-500 mb-5">
          To remove {name} access, revoke Catch&apos;s access in your Google Account settings, then reconnect with only the services you need.
        </p>
        <div className="flex justify-end gap-3">
          <a
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent(OnboardingEvents.GOOGLE_SCOPE_PERMISSIONS_CLICKED, { name })}
            className="px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
          >
            Google Permissions ↗
          </a>
          <button
            onClick={() => { trackEvent(OnboardingEvents.GOOGLE_SCOPE_INFO_CLOSED, { name }); onClose() }}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}

// iMessage can carry one phone number and one Apple ID email at once; the
// backend refuses a second of either kind (GET-17572). This modal lists every
// verified handle, lets the user drop one, and add the kind they're missing —
// keeping the compact tile uncluttered.
function IMessageHandlesModal({
  handles,
  loading,
  onRemoveHandle,
  onAddHandle,
  onClose,
}: {
  handles: IMessageHandleResponse[]
  loading: boolean
  onRemoveHandle: (identifier: string) => void
  onAddHandle: () => void
  onClose: () => void
}) {
  // Same "@ means Apple ID" test as the backend's `_is_apple_id_handle`
  // (services/imessage_verification.py), which enforces the limit.
  const hasEmail = handles.some((handle) => handle.identifier.includes('@'))
  const hasPhone = handles.some((handle) => !handle.identifier.includes('@'))
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-gray-900">Your iMessage handles</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          You can connect one phone number and one Apple ID email. To switch to a new one,
          remove the current one first.
        </p>

        <ul className="space-y-2 mb-5">
          {handles.map((handle) => (
            <li
              key={handle.identifier}
              className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{handle.identifier}</p>
                {handle.last_used_at && (
                  <p className="text-[11px] text-gray-400">
                    Last used {new Date(handle.last_used_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={() => onRemoveHandle(handle.identifier)}
                disabled={loading}
                className="flex-shrink-0 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>

        {!(hasEmail && hasPhone) && (
          <button
            onClick={onAddHandle}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Add another
          </button>
        )}
      </div>
    </div>
  )
}

// Modal listing the connected accounts (instances) of one integration, each
// with its own label and Remove control, plus an "Add another" action. Mirrors
// IMessageHandlesModal but for OAuth integrations that support multiple
// accounts (e.g. two Notion workspaces).
function IntegrationInstancesModal({
  displayName,
  defaultLabel,
  instances,
  loading,
  onRemoveInstance,
  onAddInstance,
  onClose,
}: {
  displayName: string
  defaultLabel: string
  instances: IntegrationInstance[]
  loading: boolean
  onRemoveInstance: (label: string) => void
  onAddInstance: () => void
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-sm w-full p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Your {displayName} accounts
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Your assistant can act on any of these. Remove the ones you no longer use.
        </p>

        <ul className="space-y-2 mb-5">
          {instances.map((instance) => (
            <li
              key={instance.id}
              className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {instance.label === defaultLabel ? 'Default account' : instance.label}
                </p>
                {instance.connected_at && (
                  <p className="text-[11px] text-gray-400">
                    Connected {new Date(instance.connected_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={() => onRemoveInstance(instance.label)}
                disabled={loading}
                className="flex-shrink-0 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>

        <button
          onClick={onAddInstance}
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Add another
        </button>
      </div>
    </div>
  )
}

type ModalKind =
  | 'disconnect'
  | 'scope-info'
  | 'imessage-handles'
  | 'integration-instances'
  | null

// Lets a connected tile manage a set of handles instead of a single
// disconnect (used by iMessage, where a user can own multiple handles).
interface ManageHandlesConfig {
  handles: IMessageHandleResponse[]
  onRemoveHandle: (identifier: string) => void
  onAddHandle: () => void
}

// Lets a connected OAuth tile manage multiple labeled accounts (instances)
// instead of a single disconnect.
interface ManageInstancesConfig {
  displayName: string
  // The label of the first/default connection (the integration's type value,
  // e.g. "notion"); rendered as "Default account" rather than the raw string.
  defaultLabel: string
  instances: IntegrationInstance[]
  onRemoveInstance: (label: string) => void
  onAddInstance: () => void
}

// Build a ManageInstancesConfig from an IntegrationState, or undefined when the
// integration isn't wired for multiple accounts. Tiles fall back to the normal
// single-disconnect behavior when this is undefined.
function instancesConfigFor(
  displayName: string,
  defaultLabel: string,
  state: IntegrationState,
): ManageInstancesConfig | undefined {
  if (!state.instances || !state.onRemoveInstance || !state.onAddInstance) {
    return undefined
  }
  return {
    displayName,
    defaultLabel,
    instances: state.instances,
    onRemoveInstance: state.onRemoveInstance,
    onAddInstance: state.onAddInstance,
  }
}

function IntegrationTile({
  name,
  icon,
  connected,
  loading,
  detail,
  onConnect,
  onDisconnect,
  onDownloadContact,
  googleScopeManaged,
  manageHandles,
  manageInstances,
  onManage,
}: {
  name: string
  icon: ReactNode
  connected: boolean
  loading: boolean
  detail?: string | undefined
  onConnect: () => void
  onDisconnect: () => void
  onDownloadContact?: (() => void) | undefined
  googleScopeManaged?: boolean | undefined
  manageHandles?: ManageHandlesConfig | undefined
  manageInstances?: ManageInstancesConfig | undefined
  // The general escape hatch: the owner decides what a connected tap does,
  // and renders whatever it opens. Without it a connected tile falls back to
  // the disconnect modal (or, before that, to `manageHandles` /
  // `manageInstances` / `googleScopeManaged`, each of which is a specific case
  // of this and which this deliberately outranks).
  //
  // Reach for it when "connected" is not one thing to disconnect. Google Drive
  // is today's case — it can hold a read-only Google grant and a read-write
  // Pipedream connection at once — but nothing here is Drive-specific, so the
  // next such tile should use this rather than add a fifth branch.
  onManage?: (() => void) | undefined
}) {
  const [activeModal, setActiveModal] = useState<ModalKind>(null)
  const visible = useAppTileVisible(name)

  const handleClick = () => {
    if (loading) return
    if (connected) {
      if (onManage) {
        onManage()
      } else if (manageHandles) {
        setActiveModal('imessage-handles')
      } else if (manageInstances) {
        setActiveModal('integration-instances')
      } else {
        setActiveModal(googleScopeManaged ? 'scope-info' : 'disconnect')
      }
    } else {
      onConnect()
    }
  }

  if (!visible) return null

  return (
    <>
    <button
      {...APP_TILE_ATTRIBUTE}
      onClick={handleClick}
      disabled={loading}
      className={`w-full flex flex-col items-center justify-center gap-2 p-4 rounded-xl transition-colors relative group ${
        connected
          ? 'bg-green-50 border-2 border-green-200 hover:bg-green-100'
          : 'bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm'
      } ${loading ? 'opacity-60 cursor-wait' : ''}`}
    >
      {loading ? (
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      ) : (
        <div className="w-8 h-8 flex items-center justify-center">{icon}</div>
      )}
      <span className="text-xs font-medium text-gray-700">{name}</span>
      {connected && detail && (
        <span className="text-[10px] text-gray-500 -mt-1 truncate max-w-full">{detail}</span>
      )}
      {connected && !loading && onDownloadContact && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); trackEvent(OnboardingEvents.CONTACT_CARD_DOWNLOADED, { channel: name }); onDownloadContact() }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); trackEvent(OnboardingEvents.CONTACT_CARD_DOWNLOADED, { channel: name }); onDownloadContact() } }}
          className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 transition-colors -mt-1"
        >
          <Download className="w-3 h-3" />
          Save contact
        </span>
      )}
      {connected && !loading && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" strokeWidth="3"/>
          </svg>
        </div>
      )}
    </button>
    {activeModal === 'disconnect' && (
      <DisconnectModal
        name={name}
        onConfirm={onDisconnect}
        onClose={() => setActiveModal(null)}
      />
    )}
    {activeModal === 'scope-info' && (
      <GoogleScopeInfoModal
        name={name}
        onClose={() => setActiveModal(null)}
      />
    )}
    {activeModal === 'imessage-handles' && manageHandles && (
      <IMessageHandlesModal
        handles={manageHandles.handles}
        loading={loading}
        onRemoveHandle={manageHandles.onRemoveHandle}
        onAddHandle={() => { setActiveModal(null); manageHandles.onAddHandle() }}
        onClose={() => setActiveModal(null)}
      />
    )}
    {activeModal === 'integration-instances' && manageInstances && (
      <IntegrationInstancesModal
        displayName={manageInstances.displayName}
        defaultLabel={manageInstances.defaultLabel}
        instances={manageInstances.instances}
        loading={loading}
        onRemoveInstance={manageInstances.onRemoveInstance}
        onAddInstance={() => { setActiveModal(null); manageInstances.onAddInstance() }}
        onClose={() => setActiveModal(null)}
      />
    )}
    </>
  )
}

function OtherTile({ onClick }: { onClick: () => void }) {
  if (useAppQuery() !== '') return null
  return (
    <button
      onClick={onClick}
      className="w-full flex flex-col items-center justify-center gap-2 p-4 bg-gray-50 border border-dashed border-gray-300 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
    >
      <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" strokeWidth="1.5"/>
        <line x1="12" y1="8" x2="12" y2="16" strokeWidth="1.5"/>
        <line x1="8" y1="12" x2="16" y2="12" strokeWidth="1.5"/>
      </svg>
      <span className="text-xs font-medium text-gray-400">Other</span>
    </button>
  )
}

/**
 * The request form, which for most users is the only integrations affordance
 * beyond the six hardcoded category cards — so it also has to be able to say
 * "we already support that" (GET-14034). The backend resolves the typed name
 * against the live catalog and returns `available_app` instead of filing a
 * ticket; `onConnectAvailable` hands that app to the catalog modal. A name
 * that is one of Catch's own integrations (Pipedrive, Notion) comes back as
 * `built_in_app` instead, since the catalog leaves those out (GET-20024);
 * `builtInConnector` returns the connect action for it when the page has one.
 */
export function IntegrationRequestModal({
  category,
  onClose,
  onConnectAvailable,
  builtInConnector,
  initialName = '',
}: {
  category: string
  onClose: () => void
  onConnectAvailable?: ((app: CatalogAppMatch) => void) | undefined
  builtInConnector?: ((app: BuiltInAppMatch) => (() => void) | undefined) | undefined
  initialName?: string
}) {
  const [integrationName, setIntegrationName] = useState(initialName)
  const [useCases, setUseCases] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [availableApp, setAvailableApp] = useState<CatalogAppMatch | null>(null)
  const [builtInApp, setBuiltInApp] = useState<BuiltInAppMatch | null>(null)
  const connectBuiltIn = builtInApp ? builtInConnector?.(builtInApp) : undefined

  const handleSubmit = async () => {
    if (!integrationName.trim() || !useCases.trim()) return
    setSubmitting(true)
    try {
      const response = await integrationRequestService.submit({
        integration_name: integrationName.trim(),
        use_cases: useCases.trim(),
        category,
      })
      if (response.built_in_app) {
        setBuiltInApp(response.built_in_app)
        trackEvent(OnboardingEvents.INTEGRATION_REQUEST_ALREADY_AVAILABLE, {
          category,
          app_slug: response.built_in_app.name,
          source: 'built_in',
        })
        return
      }
      if (response.available_app) {
        setAvailableApp(response.available_app)
        trackEvent(OnboardingEvents.INTEGRATION_REQUEST_ALREADY_AVAILABLE, {
          category,
          app_slug: response.available_app.app_slug,
          source: 'catalog',
        })
        return
      }
      setSubmitted(true)
      trackEvent(OnboardingEvents.INTEGRATION_REQUEST_SUBMITTED, { category })
    } catch (error) {
      console.error('Failed to submit integration request:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    trackEvent(OnboardingEvents.INTEGRATION_REQUEST_CLOSED, { category, submitted })
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Request an integration</h2>
            <p className="text-sm text-gray-500 mt-0.5">{category}</p>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {builtInApp ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {builtInApp.display_name} is already built in
            </h3>
            <p className="text-sm text-gray-500">
              {connectBuiltIn
                ? 'No need to wait — Catch connects to it directly.'
                : 'No need to wait — find it on this page to connect or manage it.'}
            </p>
            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {connectBuiltIn ? 'Not now' : 'Close'}
              </button>
              {connectBuiltIn && (
                <button
                  onClick={connectBuiltIn}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  Connect {builtInApp.display_name}
                </button>
              )}
            </div>
          </div>
        ) : availableApp ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {availableApp.name} is ready to connect
            </h3>
            <p className="text-sm text-gray-500">
              No need to wait — Catch already works with it.
            </p>
            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Not now
              </button>
              {onConnectAvailable && (
                <button
                  onClick={() => onConnectAvailable(availableApp)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  Connect {availableApp.name}
                </button>
              )}
            </div>
          </div>
        ) : submitted ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" strokeWidth="2"/>
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Request submitted</h3>
            <p className="text-sm text-gray-500">We'll review your suggestion and get back to you.</p>
            <button
              onClick={handleClose}
              className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Which integration?
              </label>
              <input
                type="text"
                value={integrationName}
                onChange={(e) => setIntegrationName(e.target.value)}
                placeholder="e.g., Notion, Monday.com, Todoist..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What use cases do you imagine?
              </label>
              <textarea
                value={useCases}
                onChange={(e) => setUseCases(e.target.value)}
                placeholder="Describe how you'd use this integration..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none text-sm"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleSubmit()}
                disabled={submitting || !integrationName.trim() || !useCases.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit request'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CategoryCard({
  icon,
  title,
  description,
  children,
  sectionId,
}: {
  icon: ReactNode
  title: string
  description: string
  children: ReactNode
  sectionId?: SettingsSectionId
}) {
  // During a search a category with no matching tile hides itself, so the
  // results are the matches and nothing else.
  const hideWhenEmpty = useHideWhenNoTiles()
  return (
    <div
      className={`${CATEGORY_CARD_CLASS} ${hideWhenEmpty}`}
      {...(sectionId ? settingsSectionAnchor(sectionId) : {})}
    >
      <div className="flex items-center gap-3 mb-5">
        {icon}
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

/**
 * Our own art for a featured app, by Pipedream slug.
 *
 * The only half of the featured list that stays in the frontend, because it is
 * the only half that is pixels rather than data. An app with no entry here
 * falls back to the descriptor's `logo_url` and then to a monogram (see
 * `FeaturedAppIcon`), so adding one is still a backend-only edit — the art is
 * an upgrade, not a precondition.
 */
const FEATURED_ICONS: Record<string, ReactNode> = {
  microsoft_teams: <MicrosoftTeamsIcon />,
  google_sheets: <GoogleSheetsIcon />,
  google_drive: <GoogleDriveIcon />,
  sharepoint: <SharePointIcon />,
  quickbooks: <QuickBooksIcon />,
  xero_accounting_api: <XeroIcon />,
  dropbox: <DropboxIcon />,
  docusign: <DocuSignIcon />,
  highlevel_oauth: <GoHighLevelIcon />,
  clio: <ClioIcon />,
}

/**
 * One renderer for the plain featured tiles, so adding a fifth app is a table
 * entry, an icon and one line at the call site — with its name read from the
 * table rather than a second copy that can drift from it.
 *
 * Module scope, not a closure inside the section: a component defined during
 * render is a new type every render, so React unmounts and remounts its whole
 * subtree each time — which showed up as a second read of the connection list
 * on mount.
 */
function FeaturedTile({
  app,
  icon,
  featured,
}: {
  app: FeaturedIntegrationSummary
  icon?: ReactNode | undefined
  featured: FeaturedPipedreamApps
}) {
  const [addingAccount, setAddingAccount] = useState(false)
  const accounts = featured.accounts(app)
  // A connected app that takes more accounts opens the same accounts list the
  // native multi-account tiles (Notion, Attio) do, rather than a disconnect.
  const manageInstances = canAddPipedreamAccount(accounts)
    ? {
      displayName: app.display_name,
      defaultLabel: app.app_slug,
      instances: asIntegrationInstances(accounts),
      onRemoveInstance: (label: string) => {
        const account = accounts.find((candidate) => candidate.label === label)
        if (account) featured.disconnectAccount(account)
      },
      onAddInstance: () => setAddingAccount(true),
    }
    : undefined
  return (
    <>
      <IntegrationTile
        name={app.display_name}
        icon={<FeaturedAppIcon app={app} localIcon={icon} />}
        connected={featured.isConnected(app)}
        loading={featured.loading || featured.isPending(app)}
        onConnect={() => featured.connect(app)}
        onDisconnect={() => featured.disconnect(app)}
        manageInstances={manageInstances}
      />
      {addingAccount && (
        <PipedreamAddAccountModal
          appName={app.display_name}
          accounts={accounts}
          onSubmit={(label) => {
            setAddingAccount(false)
            featured.connect(app, label)
          }}
          onClose={() => setAddingAccount(false)}
        />
      )}
    </>
  )
}

// ─── Main component ─────────────────────────────────────────────────

export function IntegrationCategoriesSection({
  assistantName,
  assistantPictureUrl,
  whatsapp,
  slack,
  slackPersonal,
  teamsAccess,
  sms,
  smsPhoneNumber,
  imessage,
  imessagePhoneNumber,
  assistantVoiceNumber,
  imessageHandles,
  onRemoveImessageHandle,
  zoom,
  googleMeet,
  googleDocs,
  granola,
  fathom,
  otter,
  wispr,
  clickup,
  asana,
  notion,
  hubspot,
  attio,
  zoho,
  salesforce,
  honeybook,
  customIntegrationBuilderEnabled = false,
  genericPipedreamIntegrationsEnabled = false,
  pipedreamCatalogBrowseEnabled = false,
  pendingAppConnectQuery = null,
  pendingDriveAccess = false,
  pendingWebhook,
  besideMessagingCard,
  part = 'all',
}: IntegrationCategoriesSectionProps) {
  const showMessaging = part !== 'apps'
  const showApps = part !== 'messaging'
  const [appQuery, setAppQuery] = useState('')
  const appSearch = appQuery.trim()
  // The deep links that open a catalog modal belong to the slice that shows
  // apps, so one link opens one modal when the sidebar renders both slices.
  if (!showApps) {
    pendingAppConnectQuery = null
    pendingDriveAccess = false
    pendingWebhook = null
  }
  // An app the request form resolved to a catalog entry (GET-14034). Like the
  // deep link, it names one app rather than opening the browse surface, so it
  // takes the same non-browse arm.
  const [requestedAppQuery, setRequestedAppQuery] = useState<string | null>(null)
  const catalogVisible = shouldShowPipedreamCatalog({
    capabilityEnabled: genericPipedreamIntegrationsEnabled,
    browseEnabled: pipedreamCatalogBrowseEnabled,
    pendingAppConnectQuery: pendingAppConnectQuery ?? requestedAppQuery,
  })
  const [requestCategory, setRequestCategory] = useState<string | null>(null)
  // Seeds the request form when it is opened from a catalog search that found
  // nothing, so the user does not retype the name they just searched for.
  const [requestedName, setRequestedName] = useState('')
  // Drives the Pipedream catalog modal from the suggestions row. The nonce is
  // what makes clicking the same suggestion twice reopen it.
  // Seeded rather than set from a mount effect: `pendingAppConnectQuery` is already
  // resolved before this component first renders, so an effect would only add
  // a second render here and a third in the card below.
  const [catalogRequest, setCatalogRequest] = useState<
    { query: string; nonce: number } | null
  >(() => (pendingAppConnectQuery ? { query: pendingAppConnectQuery, nonce: 1 } : null))
  const openCatalogFor = (query: string): void => {
    setCatalogRequest((current) => ({ query, nonce: (current?.nonce ?? 0) + 1 }))
  }

  // Every route into the request form goes through here, so an OPENED always
  // precedes its SUBMITTED — a SUBMITTED with no OPENED would skew the funnel
  // this change is meant to be read against.
  const openRequest = (category: string, name = '') => {
    trackEvent(OnboardingEvents.INTEGRATION_REQUEST_OPENED, { category })
    setRequestedName(name)
    setRequestCategory(category)
  }

  // Pipedream apps we show as permanent tiles in their category cards, for
  // everyone. Gated on the capability flag and NOT on the browse flag: browsing
  // is a presentational opt-in held to an internal cohort, while the capability
  // is what decides whether these connects can complete at all — and it is at
  // 100%, so in practice this is every user. With the capability off there is
  // no tile, rather than a tile that 403s on tap.
  const featured = useFeaturedPipedreamApps({
    enabled: genericPipedreamIntegrationsEnabled,
  })
  // A deep link the assistant minted for Drive opens the chooser rather than
  // the read-only scope grant — that link used to silently pick the weaker of
  // the two routes, which is the thing the chooser exists to stop.
  //
  // Opened from an effect, NOT seeded into `useState`. `pendingDriveAccess` is
  // gated on a PostHog flag, so unlike its sibling deep links it is not
  // resolved at first render: it flips false → true when the flag lands, and a
  // seeded initial value never follows, leaving the link opening nothing. The
  // ref makes it open once, so closing the chooser does not immediately
  // reopen it while the prop stays true.
  const [driveModalOpen, setDriveModalOpen] = useState(false)
  const [addingDriveAccount, setAddingDriveAccount] = useState(false)
  const [showTeamsAccess, setShowTeamsAccess] = useState(false)
  const teamsApp = featured.apps.find((app) => app.app_slug === TEAMS_APP_SLUG)
  const mondayApp = featured.apps.find((app) => app.app_slug === MONDAY_APP_SLUG)
  const driveDeepLinkOpened = useRef(false)
  useEffect(() => {
    if (!pendingDriveAccess || driveDeepLinkOpened.current) return
    driveDeepLinkOpened.current = true
    setDriveModalOpen(true)
  }, [pendingDriveAccess])
  const openDriveAccess = () => setDriveModalOpen(true)
  // The app whose descriptor says it also has a first-party read-only route.
  // Found by that field rather than by slug: which apps offer the choice is the
  // backend's call, and this file should not hold a second opinion about it.
  const driveApp = featured.apps.find((app) => app.read_only_scope_cluster)
  // The read-only route is a Google scope cluster, so it is only on offer to a
  // user who signed in with Google — `googleDocs` being null is exactly that
  // user, and they get the read-write route alone.
  const readOnlyRoute = driveApp ? googleDocs : null
  const driveConnected = (driveApp ? featured.isConnected(driveApp) : false)
    || (readOnlyRoute?.connected ?? false)

  // Every featured app in a category, as tiles. The one carrying a read-only
  // route opens the access chooser instead of connecting straight through.
  const featuredTiles = (category: string) =>
    featured.forCategory(category).filter((app) => app.app_slug !== MONDAY_APP_SLUG).map((app) => (
      app.read_only_scope_cluster
        ? (
          <IntegrationTile
            key={app.app_slug}
            name={app.display_name}
            icon={<FeaturedAppIcon app={app} localIcon={FEATURED_ICONS[app.app_slug]} />}
            connected={driveConnected}
            loading={featured.loading
              || featured.isPending(app)
              || (readOnlyRoute?.loading ?? false)}
            detail={
              featured.isConnected(app)
                ? 'Read & write'
                : driveConnected ? 'Read only' : undefined
            }
            // Both states open the chooser: with two routes of different power,
            // "connect" is a question, not an action. `onDisconnect` is the
            // honest handler for this tile but is unreachable while `onManage`
            // is set — the chooser owns the disconnect, behind the same
            // confirmation.
            onConnect={openDriveAccess}
            onDisconnect={() => featured.disconnect(app)}
            onManage={openDriveAccess}
          />
        )
        : app.app_slug === TEAMS_APP_SLUG && teamsAccess
          ? (
            <IntegrationTile
              key={app.app_slug}
              name={app.display_name}
              icon={<FeaturedAppIcon app={app} localIcon={FEATURED_ICONS[app.app_slug]} />}
              connected={featured.isConnected(app)}
              loading={featured.loading || featured.isPending(app)}
              detail={featured.isConnected(app) ? teamsAccess.detail : undefined}
              onConnect={() => featured.connect(app)}
              // Connected opens the picker (which owns the disconnect, like
              // the Slack personal tile): the connection is read-only and
              // permissioned, so "connected" is a list to manage, not one
              // thing to drop.
              onDisconnect={() => featured.disconnect(app)}
              onManage={() => setShowTeamsAccess(true)}
            />
          )
          : (
            <FeaturedTile
              key={app.app_slug}
              app={app}
              icon={FEATURED_ICONS[app.app_slug]}
              featured={featured}
            />
          )
    ))
  // Only what has actually arrived. Counting the loading state as "has tiles"
  // would answer yes for EVERY category, so a card with no featured apps and
  // no other integration renders and then vanishes — taking a step out of the
  // progress denominator with it. A card appearing one beat late is the
  // cheaper of the two, and cards that exist for other reasons never blink at
  // all.
  const hasFeatured = (category: string) =>
    genericPipedreamIntegrationsEnabled
    && featured.forCategory(category).length > 0

  const {
    byCategory: promotedByCategory,
    pendingName: promotedPending,
    error: promotedError,
    connect: connectPromoted,
    disconnect: disconnectPromoted,
  } = usePromotedIntegrations()

  // Render the promoted integrations for a category as tiles, slotted into the
  // matching hardcoded category card (e.g. Fireflies under "Note Taker").
  const promotedTiles = (category: string) =>
    (promotedByCategory.get(category) ?? []).map((it) => (
      <DynamicIntegrationTile
        key={it.name}
        integration={it}
        loading={promotedPending === it.name}
        onConnect={() => void connectPromoted(it)}
        onDisconnect={() => void disconnectPromoted(it)}
      />
    ))
  const hasPromoted = (category: string) =>
    (promotedByCategory.get(category)?.length ?? 0) > 0
  const promotedConnected = (category: string) =>
    (promotedByCategory.get(category) ?? []).some((it) => it.connected)

  // Promoted integrations whose category has no hardcoded card fall back to a
  // dedicated card so they are never silently hidden.
  const mappedCategories = new Set<string>([
    PROMOTED_CATEGORY.MESSAGING,
    PROMOTED_CATEGORY.VIDEO_CONFERENCING,
    PROMOTED_CATEGORY.NOTE_TAKER,
    PROMOTED_CATEGORY.PROJECT_MANAGEMENT,
    PROMOTED_CATEGORY.KNOWLEDGE_BASE,
    PROMOTED_CATEGORY.CRM,
    PROMOTED_CATEGORY.FINANCE,
    PROMOTED_CATEGORY.SCHEDULING,
  ])
  const unmappedPromoted = Array.from(promotedByCategory.entries()).filter(
    ([category]) => !mappedCategories.has(category),
  )

  const visibleCategories: { connected: boolean }[] = [
    { connected: whatsapp.connected || slack.connected || (slackPersonal?.connected ?? false) || (sms?.connected ?? false) || (imessage?.connected ?? false) || featured.categoryConnected(PROMOTED_CATEGORY.MESSAGING) || promotedConnected(PROMOTED_CATEGORY.MESSAGING) },
    { connected: granola.connected || fathom.connected || otter.connected || wispr.connected || featured.categoryConnected(PROMOTED_CATEGORY.NOTE_TAKER) || promotedConnected(PROMOTED_CATEGORY.NOTE_TAKER) },
  ]
  // Each `showX` is used twice — once for this progress count and once for the
  // card itself — so the bar can never disagree with what is on screen.
  const showVideoConferencing = Boolean(zoom) || Boolean(googleMeet)
    || hasPromoted(PROMOTED_CATEGORY.VIDEO_CONFERENCING)
    || hasFeatured(PROMOTED_CATEGORY.VIDEO_CONFERENCING)
  const showKnowledgeBase = Boolean(notion) || Boolean(googleDocs)
    || hasPromoted(PROMOTED_CATEGORY.KNOWLEDGE_BASE)
    || hasFeatured(PROMOTED_CATEGORY.KNOWLEDGE_BASE)
  const showProjectManagement = Boolean(clickup) || Boolean(asana)
    || hasPromoted(PROMOTED_CATEGORY.PROJECT_MANAGEMENT)
    || hasFeatured(PROMOTED_CATEGORY.PROJECT_MANAGEMENT)
  const showCrm = Boolean(hubspot) || Boolean(attio) || Boolean(zoho)
    || Boolean(salesforce) || Boolean(honeybook)
    || hasPromoted(PROMOTED_CATEGORY.CRM)
    || hasFeatured(PROMOTED_CATEGORY.CRM)
  const showFinance = hasPromoted(PROMOTED_CATEGORY.FINANCE)
    || hasFeatured(PROMOTED_CATEGORY.FINANCE)
  const showScheduling = hasPromoted(PROMOTED_CATEGORY.SCHEDULING)
    || hasFeatured(PROMOTED_CATEGORY.SCHEDULING)
  if (showVideoConferencing) visibleCategories.push({ connected: (zoom?.connected ?? false) || (googleMeet?.connected ?? false) || featured.categoryConnected(PROMOTED_CATEGORY.VIDEO_CONFERENCING) || promotedConnected(PROMOTED_CATEGORY.VIDEO_CONFERENCING) })
  if (showProjectManagement) visibleCategories.push({ connected: (clickup?.connected ?? false) || (asana?.connected ?? false) || featured.categoryConnected(PROMOTED_CATEGORY.PROJECT_MANAGEMENT) || promotedConnected(PROMOTED_CATEGORY.PROJECT_MANAGEMENT) })
  if (showKnowledgeBase) visibleCategories.push({ connected: (notion?.connected ?? false) || driveConnected || featured.categoryConnected(PROMOTED_CATEGORY.KNOWLEDGE_BASE) || promotedConnected(PROMOTED_CATEGORY.KNOWLEDGE_BASE) })
  if (showCrm) visibleCategories.push({ connected: (hubspot?.connected ?? false) || (attio?.connected ?? false) || (zoho?.connected ?? false) || (salesforce?.connected ?? false) || (honeybook?.connected ?? false) || featured.categoryConnected(PROMOTED_CATEGORY.CRM) || promotedConnected(PROMOTED_CATEGORY.CRM) })
  if (showFinance) visibleCategories.push({ connected: featured.categoryConnected(PROMOTED_CATEGORY.FINANCE) || promotedConnected(PROMOTED_CATEGORY.FINANCE) })
  if (showScheduling) visibleCategories.push({ connected: featured.categoryConnected(PROMOTED_CATEGORY.SCHEDULING) || promotedConnected(PROMOTED_CATEGORY.SCHEDULING) })

  const connectedCount = visibleCategories.filter((c) => c.connected).length
  const totalCount = visibleCategories.length

  return (
    <div>
      {part === 'apps' && (
        <div className="mb-5">
          <input
            type="search"
            value={appQuery}
            onChange={(e) => setAppQuery(e.target.value)}
            placeholder="Find an app"
            aria-label="Find an app"
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
          {appSearch !== '' && (
            <button
              type="button"
              onClick={() => (catalogVisible
                ? openCatalogFor(appSearch)
                : openRequest('Connect any app', appSearch))}
              className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {catalogVisible
                ? `Search every app for "${appSearch}"`
                : `Not listed? Ask for "${appSearch}"`}
            </button>
          )}
        </div>
      )}
      {part === 'all' && (
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Let {assistantName} handle all your admin work</h2>
        <div className="w-full bg-gray-100 rounded-full h-2 mb-1.5">
          <div
            className="bg-gradient-to-r from-primary-600 to-primary-500 h-2 rounded-full transition-[width] duration-500"
            style={{ width: `${Math.round((connectedCount / totalCount) * 100)}%` }}
          />
        </div>
        <span className="text-xs font-semibold text-primary-600">
          Unlocked {connectedCount} of {totalCount} abilities
        </span>
      </div>
      )}

      {(promotedError ?? featured.error) && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded">
          {promotedError ?? featured.error}
        </div>
      )}

      <AppTileFilter.Provider value={appQuery}>
      {/* Two columns only on the full-width single page: beside the sidebar
          a half-width card squeezes its tiles. The slices are a flex column,
          not a one-column grid, because several cards carry md:col-span-2,
          which in a grid adds a second implicit column and overlaps them. */}
      <div className={part === 'all' ? 'grid grid-cols-1 gap-5 md:grid-cols-2' : 'flex flex-col gap-5'}>
        {/* Inbox-scan suggestions. Spans both columns so its six tiles plus the
            Other tile fit on one line; renders nothing until the scan has
            something to say. */}
        {/* Gated on the capability flag, because these tiles are mostly
            Pipedream rows and a tile that cannot be connected is worse than an
            empty slot. */}
        {showApps && genericPipedreamIntegrationsEnabled && (
        <SuggestedIntegrationsCard
          surface={IntegrationSuggestionSurfaces.INTEGRATIONS_PAGE}
          // Both callbacks are handed over ONLY when the catalog is actually on
          // screen. `openCatalogFor` sets state that nothing but
          // PipedreamIntegrationsCard reads, so passing them while that card is
          // hidden gives the Other tile — and every non-inline suggestion — a
          // click that silently does nothing. That regressed once already, when
          // this row rendered without the browse surface at all; browsing is
          // now hidden for most users by `pipedreamCatalogBrowseEnabled` while
          // this row stays up, so the hazard is the normal case rather than an
          // edge one.
          // Withheld, the card degrades exactly as the onboarding step does:
          // no browse tile, and non-inline suggestions render as previews.
          onBrowseAll={catalogVisible ? () => openCatalogFor('') : undefined}
          // Only the suggestions that cannot start inline reach the parent —
          // discovered-OAuth apps, which need a resource URL only the user has.
          // Everything else connects through the shared dispatcher, using the
          // same endpoint its own tile on this page would use.
          onManualConnect={
            catalogVisible
              ? (suggestion) => openCatalogFor(suggestion.display_name)
              : undefined
          }
          // Every Pipedream surface on this page reads one shared query
          // key, so re-reading it here updates the featured tiles and the
          // connection manager together. The popup path keeps this document
          // mounted, which is why the read has to be prompted at all.
          onConnectionsRefreshRequested={featured.refresh}
        />
        )}
        {/* Messaging */}
        {showMessaging && (
        <CategoryCard
          icon={<div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-green-600" /></div>}
          title="Messaging"
          description="Chat with your assistant"
          sectionId="messaging"
        >
          <div className="grid grid-cols-3 gap-3">
            <IntegrationTile
              name="WhatsApp"
              icon={<WhatsAppIcon />}
              connected={whatsapp.connected}
              loading={whatsapp.loading}
              detail={whatsapp.detail}
              onConnect={whatsapp.onConnect}
              onDisconnect={whatsapp.onDisconnect}
            />
            <IntegrationTile
              name="Slack"
              icon={<SlackIcon />}
              connected={slack.connected}
              loading={slack.loading}
              detail={slack.detail}
              onConnect={slack.onConnect}
              onDisconnect={slack.onDisconnect}
            />
            {slackPersonal && (
              <IntegrationTile
                name="My Slack"
                icon={<SlackIcon />}
                connected={slackPersonal.connected}
                loading={slackPersonal.loading}
                detail={slackPersonal.detail}
                onConnect={slackPersonal.onConnect}
                onDisconnect={slackPersonal.onDisconnect}
                onManage={slackPersonal.onManage}
              />
            )}
            {imessage && (() => {
              const verifiedHandles = (imessageHandles ?? []).filter((h) => h.verified)
              return (
                <IntegrationTile
                  name="iMessage"
                  icon={<IMessageIcon />}
                  connected={imessage.connected}
                  loading={imessage.loading}
                  detail={
                    verifiedHandles.length > 1
                      ? `${verifiedHandles.length} handles`
                      : imessage.detail
                  }
                  onConnect={imessage.onConnect}
                  onDisconnect={imessage.onDisconnect}
                  onDownloadContact={imessagePhoneNumber ? () => void downloadVCard({
                    name: assistantName,
                    phoneNumber: imessagePhoneNumber,
                    photoUrl: assistantPictureUrl,
                    voicePhoneNumber: assistantVoiceNumber,
                  }) : undefined}
                  manageHandles={
                    verifiedHandles.length > 0 && onRemoveImessageHandle
                      ? {
                        handles: verifiedHandles,
                        onRemoveHandle: onRemoveImessageHandle,
                        onAddHandle: imessage.onConnect,
                      }
                      : undefined
                  }
                />
              )
            })()}
            {sms && (
              <IntegrationTile
                name="SMS"
                icon={<SmsIcon className="w-8 h-8 text-gray-700" />}
                connected={sms.connected}
                loading={sms.loading}
                detail={sms.detail}
                onConnect={sms.onConnect}
                onDisconnect={sms.onDisconnect}
                onDownloadContact={smsPhoneNumber ? () => void downloadVCard({
                  name: assistantName,
                  phoneNumber: smsPhoneNumber,
                  photoUrl: assistantPictureUrl,
                  voicePhoneNumber: assistantVoiceNumber,
                }) : undefined}
              />
            )}
            {featuredTiles(PROMOTED_CATEGORY.MESSAGING)}
            {promotedTiles(PROMOTED_CATEGORY.MESSAGING)}
            <OtherTile onClick={() => openRequest('Messaging')} />
          </div>
        </CategoryCard>
        )}
        {besideMessagingCard}

        {/* Video Conferencing */}
        {showApps && showVideoConferencing && (
          <CategoryCard
            icon={<div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center"><Video className="w-5 h-5 text-indigo-600" /></div>}
            title="Video Conferencing"
            description="Create & join meetings"
          >
            <div className="grid grid-cols-3 gap-3">
              {zoom && (
                <IntegrationTile
                  name="Zoom"
                  icon={<ZoomIcon />}
                  connected={zoom.connected}
                  loading={zoom.loading}
                  detail={zoom.detail}
                  onConnect={zoom.onConnect}
                  onDisconnect={zoom.onDisconnect}
                />
              )}
              {googleMeet && (
                <IntegrationTile
                  name="Google Meet"
                  icon={<GoogleIcon />}
                  connected={googleMeet.connected}
                  loading={googleMeet.loading}
                  onConnect={googleMeet.onConnect}
                  onDisconnect={googleMeet.onDisconnect}
                  googleScopeManaged
                />
              )}
              {featuredTiles(PROMOTED_CATEGORY.VIDEO_CONFERENCING)}
              {promotedTiles(PROMOTED_CATEGORY.VIDEO_CONFERENCING)}
              <OtherTile onClick={() => openRequest('Video Conferencing')} />
            </div>
          </CategoryCard>
        )}

        {/* Note Taker */}
        {showApps && (
        <CategoryCard
          icon={<div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><FileText className="w-5 h-5 text-amber-600" /></div>}
          title="Note Taker"
          description="Get context from meetings"
        >
          <div className="grid grid-cols-3 gap-3">
            <IntegrationTile
              name="Granola"
              icon={<GranolaIcon />}
              connected={granola.connected}
              loading={granola.loading}
              detail={granola.detail}
              onConnect={granola.onConnect}
              onDisconnect={granola.onDisconnect}
              manageInstances={instancesConfigFor('Granola', 'granola', granola)}
            />
            <IntegrationTile
              name="Fathom"
              icon={<FathomIcon />}
              connected={fathom.connected}
              loading={fathom.loading}
              detail={fathom.detail}
              onConnect={fathom.onConnect}
              onDisconnect={fathom.onDisconnect}
              manageInstances={instancesConfigFor('Fathom', 'fathom', fathom)}
            />
            <IntegrationTile
              name="Otter.ai"
              icon={<OtterIcon />}
              connected={otter.connected}
              loading={otter.loading}
              detail={otter.detail}
              onConnect={otter.onConnect}
              onDisconnect={otter.onDisconnect}
              manageInstances={instancesConfigFor('Otter.ai', 'otter', otter)}
            />
            <IntegrationTile
              name="Wispr Flow"
              icon={<WisprIcon />}
              connected={wispr.connected}
              loading={wispr.loading}
              detail={wispr.detail}
              onConnect={wispr.onConnect}
              onDisconnect={wispr.onDisconnect}
              manageInstances={instancesConfigFor('Wispr Flow', 'wispr', wispr)}
            />
            {featuredTiles(PROMOTED_CATEGORY.NOTE_TAKER)}
            {promotedTiles(PROMOTED_CATEGORY.NOTE_TAKER)}
            <OtherTile onClick={() => openRequest('Note Taker')} />
          </div>
        </CategoryCard>
        )}

        {/* Project Management */}
        {showApps && showProjectManagement && (
          <CategoryCard
            icon={<div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center"><ClipboardList className="w-5 h-5 text-purple-600" /></div>}
            title="Project Management"
            description="Coordinate tasks with your org"
          >
            <div className="grid grid-cols-3 gap-3">
              {clickup && (
                <IntegrationTile
                  name="ClickUp"
                  icon={<ClickUpIcon />}
                  connected={clickup.connected}
                  loading={clickup.loading}
                  detail={clickup.detail}
                  onConnect={clickup.onConnect}
                  onDisconnect={clickup.onDisconnect}
                  manageInstances={instancesConfigFor('ClickUp', 'clickup', clickup)}
                />
              )}
              {asana && (
                <IntegrationTile
                  name="Asana"
                  icon={<AsanaIcon />}
                  connected={asana.connected}
                  loading={asana.loading}
                  detail={asana.detail}
                  onConnect={asana.onConnect}
                  onDisconnect={asana.onDisconnect}
                  manageInstances={instancesConfigFor('Asana', 'asana', asana)}
                />
              )}
              {mondayApp && (
                <FeaturedTile app={mondayApp} icon={<MondayIcon />} featured={featured} />
              )}
              {featuredTiles(PROMOTED_CATEGORY.PROJECT_MANAGEMENT)}
              {promotedTiles(PROMOTED_CATEGORY.PROJECT_MANAGEMENT)}
              <OtherTile onClick={() => openRequest('Project Management')} />
            </div>
          </CategoryCard>
        )}

        {/* Knowledge Base */}
        {showApps && showKnowledgeBase && (
          <CategoryCard
            icon={<div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center"><BookOpen className="w-5 h-5 text-slate-700" /></div>}
            title="Knowledge Base"
            description="Reference your team's docs and wiki"
          >
            <div className="grid grid-cols-3 gap-3">
              {notion && (
                <IntegrationTile
                  name="Notion"
                  icon={<NotionIcon />}
                  connected={notion.connected}
                  loading={notion.loading}
                  detail={notion.detail}
                  onConnect={notion.onConnect}
                  onDisconnect={notion.onDisconnect}
                  manageInstances={instancesConfigFor('Notion', 'notion', notion)}
                />
              )}
              {googleDocs && (
                <IntegrationTile
                  name="Google Docs"
                  icon={<GoogleDocsIcon />}
                  connected={googleDocs.connected}
                  loading={googleDocs.loading}
                  onConnect={googleDocs.onConnect}
                  onDisconnect={googleDocs.onDisconnect}
                  googleScopeManaged
                />
              )}
              {/* Google Drive normally arrives via `featuredTiles` below, with
                  the chooser its descriptor asks for. This is the fallback,
                  and it keys off the descriptor's ABSENCE rather than off the
                  capability flag: the flag being on does not guarantee a
                  descriptor arrived, and a failed `/integrations/featured`
                  read would otherwise leave a Google user with no Drive tile
                  at all — including no view of the read-only grant they
                  already hold. Withheld while the list is still loading, so
                  the legacy tile does not flash before the featured one. */}
              {!driveApp && !featured.loading && googleDocs && (
                <IntegrationTile
                  name="Google Drive"
                  icon={<GoogleDriveIcon />}
                  connected={googleDocs.connected}
                  loading={googleDocs.loading}
                  detail={googleDocs.connected ? 'One grant with Google Docs' : undefined}
                  onConnect={googleDocs.onConnect}
                  onDisconnect={googleDocs.onDisconnect}
                  googleScopeManaged
                />
              )}
              {featuredTiles(PROMOTED_CATEGORY.KNOWLEDGE_BASE)}
              {promotedTiles(PROMOTED_CATEGORY.KNOWLEDGE_BASE)}
              <OtherTile onClick={() => openRequest('Knowledge Base')} />
            </div>
          </CategoryCard>
        )}

        {/* CRM */}
        {showApps && showCrm && (
          <CategoryCard
            icon={<div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center"><Briefcase className="w-5 h-5 text-orange-600" /></div>}
            title="CRM"
            description="Read and update your sales pipeline"
          >
            <div className="grid grid-cols-3 gap-3">
              {hubspot && (
                <IntegrationTile
                  name="HubSpot"
                  icon={<HubSpotIcon />}
                  connected={hubspot.connected}
                  loading={hubspot.loading}
                  detail={hubspot.detail}
                  onConnect={hubspot.onConnect}
                  onDisconnect={hubspot.onDisconnect}
                  manageInstances={instancesConfigFor('HubSpot', 'hubspot', hubspot)}
                />
              )}
              {attio && (
                <IntegrationTile
                  name="Attio"
                  icon={<AttioIcon />}
                  connected={attio.connected}
                  loading={attio.loading}
                  detail={attio.detail}
                  onConnect={attio.onConnect}
                  onDisconnect={attio.onDisconnect}
                  manageInstances={instancesConfigFor('Attio', 'attio', attio)}
                />
              )}
              {zoho && (
                <IntegrationTile
                  name="Zoho"
                  icon={<ZohoIcon />}
                  connected={zoho.connected}
                  loading={zoho.loading}
                  detail={zoho.detail}
                  onConnect={zoho.onConnect}
                  onDisconnect={zoho.onDisconnect}
                  manageInstances={instancesConfigFor('Zoho', 'zoho', zoho)}
                />
              )}
              {salesforce && (
                <IntegrationTile
                  name="Salesforce"
                  icon={<SalesforceIcon />}
                  connected={salesforce.connected}
                  loading={salesforce.loading}
                  detail={salesforce.detail}
                  onConnect={salesforce.onConnect}
                  onDisconnect={salesforce.onDisconnect}
                  manageInstances={instancesConfigFor('Salesforce', 'salesforce', salesforce)}
                />
              )}
              {honeybook && (
                <IntegrationTile
                  name="HoneyBook"
                  icon={<HoneyBookIcon />}
                  connected={honeybook.connected}
                  loading={honeybook.loading}
                  detail={honeybook.detail}
                  onConnect={honeybook.onConnect}
                  onDisconnect={honeybook.onDisconnect}
                  manageInstances={instancesConfigFor('HoneyBook', 'honeybook', honeybook)}
                />
              )}
              {featuredTiles(PROMOTED_CATEGORY.CRM)}
              {promotedTiles(PROMOTED_CATEGORY.CRM)}
              <OtherTile onClick={() => openRequest('CRM')} />
            </div>
          </CategoryCard>
        )}

        {/* Finance */}
        {showApps && showFinance && (
          <CategoryCard
            icon={<div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Wallet className="w-5 h-5 text-emerald-600" /></div>}
            title="Finance"
            description="Track spend, bills and your books"
          >
            <div className="grid grid-cols-3 gap-3">
              {featuredTiles(PROMOTED_CATEGORY.FINANCE)}
              {promotedTiles(PROMOTED_CATEGORY.FINANCE)}
              <OtherTile onClick={() => openRequest('Finance')} />
            </div>
          </CategoryCard>
        )}

        {/* Scheduling */}
        {showApps && showScheduling && (
          <CategoryCard
            icon={<div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center"><CalendarClock className="w-5 h-5 text-sky-600" /></div>}
            title="Scheduling"
            description="See what people book through your links"
          >
            <div className="grid grid-cols-3 gap-3">
              {featuredTiles(PROMOTED_CATEGORY.SCHEDULING)}
              {promotedTiles(PROMOTED_CATEGORY.SCHEDULING)}
              <OtherTile onClick={() => openRequest('Scheduling')} />
            </div>
          </CategoryCard>
        )}

        {/* Promoted integrations whose category has no hardcoded card above */}
        {showApps && unmappedPromoted.map(([category, list]) => (
          <PromotedCategoryCard
            key={category}
            category={category}
            integrations={list}
            pendingName={promotedPending}
            onConnect={(it) => void connectPromoted(it)}
            onDisconnect={(it) => void disconnectPromoted(it)}
          />
        ))}

        {/* The connection manager follows the capability flag so a successful
            suggestion connect remains visible and removable even when the full
            browse catalog is withheld. Its connected-only surface self-hides
            when empty; the separate presentational flag controls only browsing.
            `catalogVisible` also opens for a deep-linked app (GET-12087) — the
            card hard-gates BOTH its modal and its openRequest effect on
            `catalogBrowseEnabled`, so a link the assistant sent would otherwise
            be swallowed for exactly the users who cannot browse. */}
        {part === 'apps' && (
          <h3 className="mt-4 -mb-2 text-sm font-semibold text-gray-900">
            Can't find it?
          </h3>
        )}
        {showApps && genericPipedreamIntegrationsEnabled && (
          <PipedreamIntegrationsCard
            catalogBrowseEnabled={catalogVisible}
            openRequest={catalogVisible ? catalogRequest : null}
            onRequestIntegration={(searchedName) => {
              openRequest('Connect any app', searchedName)
            }}
            managedElsewhere={featured.apps.map((app) => app.app_slug)}
          />
        )}

        {/* Integrations an admin shared with this user — ungated, self-hides when none */}
        {showApps && <SharedIntegrationsCard />}

        {/* Custom (toolsmith) integrations — only when the user has access to the builder */}
        {showApps && customIntegrationBuilderEnabled && <CustomIntegrationsCard />}

        {/* Other Integrations */}
        {showApps && <OtherIntegrationsCard pendingWebhook={pendingWebhook} />}
      </div>
      </AppTileFilter.Provider>

      {requestCategory && (
        <IntegrationRequestModal
          category={requestCategory}
          initialName={requestedName}
          onClose={() => setRequestCategory(null)}
          builtInConnector={(app) => {
            const promoted = app.promoted
              ? Array.from(promotedByCategory.values()).flat().find((it) => it.name === app.name)
              : undefined
            if (!promoted || promoted.connected) return undefined
            return () => {
              setRequestCategory(null)
              void connectPromoted(promoted)
            }
          }}
          onConnectAvailable={genericPipedreamIntegrationsEnabled
            ? (app) => {
                setRequestCategory(null)
                // Naming an app is a connect path, not browsing — the same arm
                // the `?connect_app=` deep link takes, so the browse flag must
                // not gate it (see `shouldShowPipedreamCatalog`).
                setRequestedAppQuery(app.name)
                openCatalogFor(app.name)
              }
            : undefined}
        />
      )}

      {/* Needs the descriptor, so a deep link that arrives before the list has
          loaded waits for it rather than opening a chooser with no app behind
          the read-write route. */}
      {driveModalOpen && driveApp && (
        <GoogleDriveAccessModal
          icon={<FeaturedAppIcon app={driveApp} localIcon={FEATURED_ICONS[driveApp.app_slug]} />}
          readOnly={readOnlyRoute ? {
            connected: readOnlyRoute.connected,
            loading: readOnlyRoute.loading,
            onConnect: () => { setDriveModalOpen(false); readOnlyRoute.onConnect() },
          } : null}
          fullAccess={{
            connected: featured.isConnected(driveApp),
            loading: featured.loading || featured.isPending(driveApp),
            onConnect: () => featured.connect(driveApp),
            onDisconnect: () => featured.disconnect(driveApp),
            accounts: featured.accounts(driveApp),
            onAddAccount: () => { setDriveModalOpen(false); setAddingDriveAccount(true) },
            onDisconnectAccount: featured.disconnectAccount,
          }}
          onClose={() => setDriveModalOpen(false)}
        />
      )}
      {addingDriveAccount && driveApp && (
        <PipedreamAddAccountModal
          appName={driveApp.display_name}
          accounts={featured.accounts(driveApp)}
          onSubmit={(label) => {
            setAddingDriveAccount(false)
            featured.connect(driveApp, label)
          }}
          onClose={() => setAddingDriveAccount(false)}
        />
      )}
      {teamsAccess && (
        <TeamsAccessModal
          isOpen={showTeamsAccess}
          onClose={() => setShowTeamsAccess(false)}
          onDisconnect={async () => {
            if (teamsApp) featured.disconnect(teamsApp)
          }}
          onSaved={teamsAccess.onSaved}
        />
      )}
    </div>
  )
}
